import { UnreachableCaseError } from 'ts-essentials';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { getCharacterByIncrementedIndex } from '../../../utils/character';
import {
  IAliasableSlice,
  IAxisSettings,
  IDisplayedMeasure,
  IDisplayedSlice,
  IDisplayedSliceSetting,
  YAxisPosition,
} from '../interfaces';
import { IAliasableMeasureControl } from '../interfaces';
import { IDisplayedMeasureSetting } from '../interfaces';

export const queryTypesUseBaseData = [
  QueryType.Significance,
  QueryType.CohortSample,
  QueryType.Prediction,
];

export const getMeasureLabel = (
  queryType: QueryType.Expression | QueryType.Funnel | QueryType.DataTable,
  measureIndex: number,
  isResultKey: boolean,
  hasResultMeasure: boolean,
): string => {
  if (queryType === QueryType.Expression) {
    // When there is no result measure - assign a letter to each measure
    if (!hasResultMeasure) {
      return getCharacterByIncrementedIndex('A', measureIndex);
    }

    // If result measure exists, it will be first element in array.
    // Therefore, in order to generate correct letter, we must
    // decrement measureIndex by 1
    if (!isResultKey) {
      return getCharacterByIncrementedIndex('A', measureIndex - 1);
    }

    return '';
  } else if (queryType === QueryType.Funnel) {
    return String(measureIndex + 1);
  } else if (queryType === QueryType.DataTable) {
    return '';
  } else {
    throw new UnreachableCaseError(queryType);
  }
};

// generate DisplayedMeasures from MeasureControls and MeasureSettings
// also update format if user decided to apply AxisFormat to the chart
export const getDisplayedMeasures = ({
  displayedMeasureSettings,
  measureControls,
  queryType,
  axisSettings,
}: {
  displayedMeasureSettings: IDisplayedMeasureSetting[];
  measureControls: IAliasableMeasureControl[];
  queryType: QueryType.Expression | QueryType.Funnel | QueryType.DataTable;
  axisSettings: IAxisSettings;
}): IDisplayedMeasure[] => {
  const hasResultMeasure = measureControls.some(
    (measureSetting) => measureSetting.isResultKey,
  );

  const { yAxisSettings } = axisSettings;

  return displayedMeasureSettings.reduce<IDisplayedMeasure[]>(
    (result, measureSetting, index) => {
      const foundMeasure = measureControls.find(
        (currMeasure) => currMeasure.key === measureSetting.key,
      );
      if (foundMeasure) {
        const measureLabel = getMeasureLabel(
          queryType,
          index,
          foundMeasure.isResultKey,
          hasResultMeasure,
        );

        let format = measureSetting.format;
        const yAxisPosition =
          measureSetting.yAxisPosition ?? YAxisPosition.LEFT;

        //  Y-Axis format can override the measure format
        if (yAxisSettings[yAxisPosition].isApplyYAxisFormat) {
          format = {
            __typename: 'MeasureDisplayFormat',
            kind: yAxisSettings[yAxisPosition].format.kind,
            decimalPlace: yAxisSettings[yAxisPosition].format.decimalPlace,
            calculationUnit: null,
            calculationValue: null,
            unit: null,
          };
        }

        result.push({
          ...measureSetting,
          format,
          name: foundMeasure.name,
          charts: foundMeasure.charts,
          userAlias: foundMeasure.userAlias,
          hasUserAlias: foundMeasure.hasUserAlias,
          label: measureLabel,
          isResultKey: foundMeasure.isResultKey,
          yAxisPosition,
        });
      }
      return result;
    },
    [],
  );
};

export const getDisplayedSlices = (
  displayedSliceSettings: IDisplayedSliceSetting[],
  slices: IAliasableSlice[],
) => {
  // KBT-840 filter out comparison slices
  const filteredSlices = slices.filter((slice) => !slice.isComparison);

  return displayedSliceSettings.reduce<IDisplayedSlice[]>(
    (result, sliceSetting) => {
      const foundSlice = filteredSlices.find(
        (currSlice) => currSlice.key === sliceSetting.key,
      );
      if (foundSlice) {
        result.push({
          ...sliceSetting,
          hint: foundSlice.hint,
          name: foundSlice.name,
          userAlias: foundSlice.userAlias,
          hasUserAlias: foundSlice.hasUserAlias,
          rank: foundSlice.rank,
          originalHint: foundSlice.originalHint,
          isComparison: foundSlice.isComparison,
          originalSliceKey: foundSlice.originalSliceKey,
        });
      }
      return result;
    },
    [],
  );
};
